import { ServiceItem } from '../../types';
import { selectBillableServices } from '../../services/billingSelection';
import {
  EinweisungGruppe,
  EinweisungOnlineTeilnahme,
  EinweisungTeilnehmer,
  SigeEinweisung,
} from '../sige-plan/types';
import { SeitenSchaetzung } from '../../services/sigeEinweisung';

/**
 * Die Regeln der Einweisungs-Oberflaeche — ohne React, ohne DOM, ohne Supabase.
 *
 * WARUM hier: das Projekt hat keine jsdom-Testumgebung, Komponenten sind also
 * nicht pruefbar. Es gilt die stehende Entscheidung „duenner Container, Logik
 * in Hooks + pure Funktionen". Alles, was in dieser Oberflaeche eine
 * ENTSCHEIDUNG trifft — welche Punkte auf dem Blatt bleiben, welche LV-Position
 * zur Buchung ansteht, ob ueberhaupt noch gebucht werden darf, welche Datei ein
 * Papier-Scan sein kann —, steht deshalb hier und in einweisungLogic.test.ts.
 *
 * Die fachlichen Regeln des BLATTES (Vorschlag, Kuerzung, Seitenschaetzung,
 * laufende Nummer) liegen dagegen in src/services/sigeEinweisung.ts. Dieses
 * Modul ergaenzt sie um das, was nur die Bedienung angeht.
 */

// ─── Gruppen und Punkte ─────────────────────────────────────────────────────

/**
 * Ersetzt eine Gruppe an ihrer Stelle. Alle Aenderungsfunktionen laufen
 * hierueber, damit die Reihenfolge der Gruppen nie durch eine Aenderung an
 * einem einzelnen Punkt verrutscht.
 */
const ersetzeGruppe = (
  gruppen: EinweisungGruppe[],
  gruppenId: string,
  aendere: (g: EinweisungGruppe) => EinweisungGruppe,
): EinweisungGruppe[] => (gruppen ?? []).map((g) => (g.id === gruppenId ? aendere(g) : g));

/** Schreibt den Text eines Punktes. Ein leerer Text bleibt stehen — geloescht wird nur ueber das Minus. */
export const setzePunkt = (
  gruppen: EinweisungGruppe[],
  gruppenId: string,
  index: number,
  text: string,
): EinweisungGruppe[] =>
  ersetzeGruppe(gruppen, gruppenId, (g) => ({
    ...g,
    punkte: g.punkte.map((p, i) => (i === index ? text : p)),
  }));

/** Haengt einen Punkt an. Ohne Text entsteht eine leere Zeile, die sofort den Fokus bekommt. */
export const fuegePunktHinzu = (
  gruppen: EinweisungGruppe[],
  gruppenId: string,
  text = '',
): EinweisungGruppe[] =>
  ersetzeGruppe(gruppen, gruppenId, (g) => ({ ...g, punkte: [...g.punkte, text] }));

export const entfernePunkt = (
  gruppen: EinweisungGruppe[],
  gruppenId: string,
  index: number,
): EinweisungGruppe[] =>
  ersetzeGruppe(gruppen, gruppenId, (g) => ({
    ...g,
    punkte: g.punkte.filter((_, i) => i !== index),
  }));

/**
 * Verschiebt einen Punkt um eine Stelle. Am Rand passiert NICHTS — bewusst
 * kein Umlauf ans andere Ende: ein Punkt, der beim Klick auf „hoch" ganz nach
 * unten springt, sieht aus wie ein Fehler.
 */
export const verschiebePunkt = (
  gruppen: EinweisungGruppe[],
  gruppenId: string,
  index: number,
  richtung: -1 | 1,
): EinweisungGruppe[] =>
  ersetzeGruppe(gruppen, gruppenId, (g) => {
    const ziel = index + richtung;
    if (index < 0 || index >= g.punkte.length || ziel < 0 || ziel >= g.punkte.length) return g;
    const punkte = [...g.punkte];
    [punkte[index], punkte[ziel]] = [punkte[ziel], punkte[index]];
    return { ...g, punkte };
  });

export const setzeGruppenTitel = (
  gruppen: EinweisungGruppe[],
  gruppenId: string,
  titel: string,
): EinweisungGruppe[] => ersetzeGruppe(gruppen, gruppenId, (g) => ({ ...g, titel }));

/** Neue Gruppe ans Ende. Die `id` kommt von aussen, damit die Funktion pur bleibt. */
export const fuegeGruppeHinzu = (
  gruppen: EinweisungGruppe[],
  id: string,
  titel: string,
): EinweisungGruppe[] => [...(gruppen ?? []), { id, titel, punkte: [] }];

export const entferneGruppe = (gruppen: EinweisungGruppe[], gruppenId: string): EinweisungGruppe[] =>
  (gruppen ?? []).filter((g) => g.id !== gruppenId);

/**
 * Der Vorschlag ERSETZT die Gruppen nicht, er ergaenzt sie.
 *
 * WARUM: der Knopf „Vorschlag aus dem Plan" wird auch dann gedrueckt, wenn
 * schon von Hand getippt wurde — ein Ersetzen wuerde diese Arbeit
 * kommentarlos wegwerfen. Bekannte Gruppen (gleiche id) bekommen nur die
 * Punkte dazu, die sie noch nicht haben; unbekannte kommen ans Ende.
 *
 * Verglichen wird auf einer groben Normalform, weil derselbe Punkt nach einer
 * Handkorrektur anders getippt dasteht („Notruf 112." / „Notruf 112").
 */
export const uebernimmVorschlag = (
  bestehende: EinweisungGruppe[],
  vorschlag: EinweisungGruppe[],
): EinweisungGruppe[] => {
  const alt = bestehende ?? [];
  const neu = vorschlag ?? [];
  if (alt.length === 0) return neu.map((g) => ({ ...g, punkte: [...g.punkte] }));

  const ergebnis = alt.map((g) => {
    const passend = neu.find((n) => n.id === g.id);
    if (!passend) return g;
    const gesehen = new Set(g.punkte.map(vergleichsform));
    const zusatz = passend.punkte.filter((p) => {
      const key = vergleichsform(p);
      if (!key || gesehen.has(key)) return false;
      gesehen.add(key);
      return true;
    });
    return zusatz.length > 0 ? { ...g, punkte: [...g.punkte, ...zusatz] } : g;
  });

  const bekannt = new Set(alt.map((g) => g.id));
  const neueGruppen = neu.filter((n) => !bekannt.has(n.id)).map((g) => ({ ...g, punkte: [...g.punkte] }));
  return [...ergebnis, ...neueGruppen];
};

/** Grobe Vergleichsform: klein, ohne Satzzeichen, ohne doppelte Leerzeichen. */
const vergleichsform = (text: string): string =>
  (text || '')
    .toLowerCase()
    .replace(/[.,;:!?„“"'()\[\]–—-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Raeumt vor dem Speichern auf: leere Punkte fallen weg, danach leere Gruppen.
 *
 * Eine Ueberschrift ohne einen einzigen Punkt sieht auf dem unterschriebenen
 * Blatt aus wie ein Druckfehler (dieselbe Begruendung wie beim Vorschlag in
 * sigeEinweisung.ts). Beim TIPPEN wird dagegen nicht aufgeraeumt — sonst
 * verschwaende die gerade angelegte leere Zeile unter dem Cursor.
 */
export const bereinigeGruppen = (gruppen: EinweisungGruppe[]): EinweisungGruppe[] =>
  (gruppen ?? [])
    .map((g) => ({ ...g, titel: (g.titel || '').trim(), punkte: g.punkte.map((p) => (p || '').trim()).filter(Boolean) }))
    .filter((g) => g.punkte.length > 0);

/** Alle Punkte aller Gruppen — Grundlage der Zaehlanzeige. */
export const zaehlePunkte = (gruppen: EinweisungGruppe[]): number =>
  (gruppen ?? []).reduce((summe, g) => summe + (g.punkte ?? []).length, 0);

// ─── Teilnehmer ─────────────────────────────────────────────────────────────

export const neuerTeilnehmer = (id: string, name = '', firma = ''): EinweisungTeilnehmer => ({
  id,
  name: name.trim(),
  firma: firma.trim() || undefined,
});

export const setzeTeilnehmerFeld = (
  liste: EinweisungTeilnehmer[],
  id: string,
  feld: 'name' | 'firma',
  wert: string,
): EinweisungTeilnehmer[] => (liste ?? []).map((t) => (t.id === id ? { ...t, [feld]: wert } : t));

export const entferneTeilnehmer = (liste: EinweisungTeilnehmer[], id: string): EinweisungTeilnehmer[] =>
  (liste ?? []).filter((t) => t.id !== id);

/**
 * Setzt die Unterschrift eines Teilnehmers.
 *
 * Ein leerer DataURL LOESCHT sie samt Zeitstempel — sonst bliebe nach einem
 * „Unterschrift verwerfen" ein Zeitpunkt stehen, zu dem es keine Unterschrift
 * mehr gibt.
 */
export const setzeUnterschrift = (
  liste: EinweisungTeilnehmer[],
  id: string,
  dataUrl: string,
  jetztIso: string,
): EinweisungTeilnehmer[] =>
  (liste ?? []).map((t) =>
    t.id !== id
      ? t
      : dataUrl
        ? { ...t, unterschrift: dataUrl, unterschriebenAm: jetztIso }
        : { ...t, unterschrift: undefined, unterschriebenAm: undefined },
  );

export const zaehleUnterschriften = (liste: EinweisungTeilnehmer[]): number =>
  (liste ?? []).filter((t) => !!t.unterschrift).length;

/** Teilnehmer ohne jeden Namen zaehlen nicht — sie stuenden als leere Zeile auf dem Blatt. */
export const bereinigeTeilnehmer = (liste: EinweisungTeilnehmer[]): EinweisungTeilnehmer[] =>
  (liste ?? [])
    .map((t) => ({ ...t, name: (t.name || '').trim(), firma: (t.firma || '').trim() || undefined }))
    .filter((t) => t.name.length > 0);

/**
 * Groesste zulaessige Zahl an Blanko-Zeilen.
 *
 * NICHT frei gewaehlt: die Datenbank prueft `leerzeilen between 0 and 40`
 * (supabase/sige_einweisungen.sql). Beide Zahlen muessen dieselbe sein.
 */
export const LEERZEILEN_MAX = 40;

/**
 * Der eingetippte Wert des Blanko-Zeilen-Feldes, eingeklemmt auf das, was die
 * Datenbank annimmt.
 *
 * WARUM im Feld und nicht erst beim Speichern: das Speichern schreibt Punkte,
 * Teilnehmer und Kopfdaten in EINEM Update. Eine 50 in diesem Nebenfeld haette
 * das ganze Blatt scheitern lassen — mit dem rohen Text der Datenbankpruefung
 * als Meldung, auf der Baustelle, mit fuenfzehn geleisteten Unterschriften im
 * Speicher. Eine 50 ist ausserdem nie gemeint: mehr als 40 Nachzuegler passen
 * auf kein Blatt.
 */
export const klemmeLeerzeilen = (roh: string | number | null | undefined): number => {
  const ziffern = String(roh ?? '').replace(/\D/g, '');
  if (ziffern === '') return 0;
  return Math.min(LEERZEILEN_MAX, Number(ziffern));
};

// ─── Anzeige ────────────────────────────────────────────────────────────────

export interface SeitenAnzeige {
  ton: 'ok' | 'warnung';
  text: string;
}

/**
 * Der Text neben dem Punkte-Editor.
 *
 * Faerbt nur, sperrt nie — die verbindliche Seitenzahl kennt erst der
 * PDF-Generator (siehe schaetzeSeiten).
 */
export const beschreibeSeiten = (schaetzung: SeitenSchaetzung, maxSeiten = 2): SeitenAnzeige => {
  if (schaetzung.passt) {
    return {
      ton: 'ok',
      text: schaetzung.seitenInhalt <= 1 ? 'Passt auf 1 Seite' : `Passt auf ${schaetzung.seitenInhalt} Seiten`,
    };
  }
  const zeilen = Math.max(1, schaetzung.ueberhang);
  return {
    ton: 'warnung',
    text: `${zeilen} ${zeilen === 1 ? 'Zeile' : 'Zeilen'} zu viel für ${maxSeiten} Seiten`,
  };
};

/**
 * Dieselbe Anzeige aus der VERBINDLICHEN Zahl des PDF-Generators
 * (`zaehleEinweisungSeiten`).
 *
 * Sie loest die Schaetzung ab, sobald sie da ist — sie kommt verzoegert, weil
 * sie das Dokument wirklich setzt. Der Unterschied im Text ist Absicht: die
 * Schaetzung spricht von Zeilen (mehr weiss sie nicht), die genaue Zahl von
 * Seiten. „Gesamt" nennt die Unterschriftenliste mit, die ueber die zwei
 * Inhaltsseiten hinaus laufen DARF — begrenzt ist nur der Inhalt.
 */
export const beschreibeSeitenGenau = (
  seiten: { seitenInhalt: number; seitenGesamt: number; passt: boolean },
  maxSeiten = 2,
): SeitenAnzeige => {
  const gesamt = seiten.seitenGesamt > seiten.seitenInhalt ? ` (${seiten.seitenGesamt} mit Unterschriften)` : '';
  if (seiten.passt) {
    return {
      ton: 'ok',
      text: `Passt auf ${seiten.seitenInhalt} ${seiten.seitenInhalt === 1 ? 'Seite' : 'Seiten'}${gesamt}`,
    };
  }
  return {
    ton: 'warnung',
    text: `${seiten.seitenInhalt} Seiten Inhalt — vorgesehen sind ${maxSeiten}${gesamt}`,
  };
};

/** Kopfzeile einer Einweisung in der Ablage: „Nr. 3 — 14.08.2026". */
export const einweisungTitel = (e: Pick<SigeEinweisung, 'nummer' | 'titel'>): string =>
  (e.titel || '').trim() || `Einweisung Nr. ${e.nummer}`;

export const beschreibeQuelle = (e: Pick<SigeEinweisung, 'quelle'>): string =>
  e.quelle === 'papier' ? 'Papier (Scan)' : 'Digital';

/** ISO-Datum (YYYY-MM-DD) deutsch. Unlesbares bleibt unveraendert stehen statt „Invalid Date". */
export const formatDatum = (iso: string | null | undefined): string => {
  const s = (iso || '').slice(0, 10);
  const treffer = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return treffer ? `${treffer[3]}.${treffer[2]}.${treffer[1]}` : (iso || '—');
};

/** Heute als ISO-Datum, ohne Zeitzonenversatz (toISOString kippt abends auf den Vortag). */
export const heuteIso = (jetzt: Date = new Date()): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${jetzt.getFullYear()}-${p(jetzt.getMonth() + 1)}-${p(jetzt.getDate())}`;
};

/**
 * Der vorbelegte Durchfuehrende: der angemeldete Benutzer.
 *
 * Vor- und Nachname, sonst der Firmenname, sonst leer. NIE die E-Mail-Adresse —
 * sie stuende so auf einem unterschriebenen Blatt.
 */
export const vorbelegterDurchfuehrender = (
  profil: { first_name?: string; last_name?: string; company_name?: string } | null | undefined,
): string => {
  const name = [profil?.first_name, profil?.last_name].filter((t) => !!t?.trim()).join(' ').trim();
  return name || (profil?.company_name || '').trim();
};

// ─── Abrechnung ─────────────────────────────────────────────────────────────

/** Ist diese Einweisung bereits auf eine LV-Position gebucht? */
export const istGebucht = (e: Pick<SigeEinweisung, 'gebucht_am'> | null | undefined): boolean =>
  !!e?.gebucht_am;

/**
 * DER RIEGEL GEGEN DIE DOPPELBUCHUNG.
 *
 * Gebucht wird GENAU EINMAL je Einweisung — beim Abschliessen bzw. direkt nach
 * dem Papier-Upload. Ausdruecklich NICHT beim PDF-Export: der SiGe-Plan bucht
 * beim Export, weil er einmal herausgeht; das Einweisungsblatt wird dagegen
 * mehrfach gedruckt (Aushang, Ordner, Nachzuegler), und jeder weitere Druck
 * wuerde die LV-Position ein zweites Mal belasten.
 *
 * Der harte Riegel sitzt in der Datenbank (`markiereGebucht` schreibt nur bei
 * `gebucht_am IS NULL`). Diese Funktion entscheidet nur, ob der Dialog
 * ueberhaupt erscheint — im Lesemodus nie.
 */
export const darfBuchungAnbieten = (
  e: Pick<SigeEinweisung, 'gebucht_am'> | null | undefined,
  isReadOnly: boolean,
  /**
   * Das GESAMTE Leistungsverzeichnis des Projekts, nicht die gefilterte
   * Auswahl: hat das LV keine passende Position, soll der Dialog trotzdem
   * kommen — mit der Zeile „ohne Leistungsbuchung". Genau so verhalten sich
   * Vorankuendigung und Unterlage. Erst ein Projekt GANZ ohne LV hat nichts zu
   * fragen.
   */
  alleServices: ServiceItem[],
): boolean => !!e && !isReadOnly && !istGebucht(e) && (alleServices ?? []).length > 0;

/**
 * Darf die Abrechnung in der ABLAGE nachgeholt werden?
 *
 * WARUM es diesen zweiten Einstieg braucht: die Frage kommt sonst genau
 * einmal — beim Abschliessen bzw. nach dem Papier-Upload. Wer sie wegklickt
 * (Escape, X, versehentlich „ohne Leistungsbuchung"), kommt nicht mehr an sie
 * heran: ein abgeschlossenes Blatt hat keinen Abschliessen-Knopf mehr. Die
 * gehaltene, unterschriebene Einweisung fiele damit still aus der Abrechnung.
 *
 * Fuer ABGESCHLOSSENE Blaetter: ein digitaler Entwurf kommt ohnehin noch
 * einmal am Abschliessen vorbei, und ein zweiter Knopf daneben waere nur die
 * Einladung, zu frueh zu buchen.
 *
 * UND fuer die PAPIERFORM mit hochgeladenem Scan, auch wenn sie noch im
 * Entwurf steht: die Einweisung IST dort bereits gehalten und unterschrieben,
 * das Blatt liegt als Datei vor. Ohne diesen Zweig bliebe genau die Luecke
 * offen, die diese Regel schliessen soll — wer die Frage nach dem Upload
 * wegklickt, faende den zweiten Einstieg nur ueber den Umweg „Blatt oeffnen
 * und abschliessen", und darauf kommt niemand.
 *
 * Der Riegel gegen die Doppelbuchung bleibt unveraendert `markiereGebucht` in
 * der Datenbank — diese Regel entscheidet nur, ob der Knopf erscheint.
 */
export const darfNachbuchen = (
  // `quelle`/`dokument_pfad` sind optional: fehlen sie, bleibt es beim
  // Abschluss-Kriterium. Als Pflichtfelder zwaengen sie jedem Aufrufer zwei
  // Angaben auf, die fuer den digitalen Weg nichts entscheiden.
  e:
    | (Pick<SigeEinweisung, 'gebucht_am' | 'status'>
      & Partial<Pick<SigeEinweisung, 'quelle' | 'dokument_pfad'>>)
    | null
    | undefined,
  isReadOnly: boolean,
  alleServices: ServiceItem[],
): boolean => {
  const belegt = e?.status === 'abgeschlossen'
    || (e?.quelle === 'papier' && !!e?.dokument_pfad?.trim());
  return belegt && darfBuchungAnbieten(e, isReadOnly, alleServices);
};

/**
 * Welche LV-Positionen der Abrechnungsdialog anbietet.
 *
 * ABSICHTLICH nur eine Weiterleitung an `selectBillableServices` und keine
 * eigene Regel: das ist DIE eine Stelle, die entscheidet, welche Position ein
 * Dokumentmodul buchen darf, und sie kennt die Einweisung. Eine zweite Regel
 * hier waere genau die Doppelung, gegen die jenes Modul geschrieben wurde —
 * sie wuerde driften, und zwar zuerst an den Stichworten (dort steht
 * „unterweisung" mit Begruendung NICHT drin: es faengt die DGUV-V2-Unterweisung
 * der Fachkraft fuer Arbeitssicherheit ein, eine voellig andere Leistung).
 *
 * OHNE `fallbackToAllServices`, anders als der SiGe-Plan-Editor: findet die
 * Regel nichts, bleibt dem Anwender die Zeile „ohne Leistungsbuchung" — wie
 * bei Vorankuendigung und Unterlage. Lieber nichts anbieten als das Falsche.
 */
export const waehleEinweisungsPositionen = (services: ServiceItem[]): ServiceItem[] =>
  selectBillableServices(services ?? [], 'einweisung');

// ─── Papier-Upload ──────────────────────────────────────────────────────────

/** Was der Dateidialog anbietet. Scan als PDF, Foto vom Handy als Bild. */
export const PAPIER_DATEI_TYPEN = 'application/pdf,image/*';

/** Groesste zulaessige Dateigroesse (20 MB) — ein Handyfoto liegt weit darunter. */
export const PAPIER_MAX_BYTES = 20 * 1024 * 1024;

export interface DateiPruefung {
  ok: boolean;
  fehler?: string;
}

/**
 * Prueft die gewaehlte Datei, BEVOR sie hochgeladen wird.
 *
 * Der MIME-Typ allein reicht nicht: Windows liefert fuer manche Scanner-PDFs
 * einen leeren `type`. Deshalb faellt die Pruefung auf die Dateiendung zurueck,
 * statt eine gueltige Datei abzulehnen.
 */
export const pruefePapierDatei = (
  datei: { name?: string; type?: string; size?: number } | null | undefined,
): DateiPruefung => {
  if (!datei) return { ok: false, fehler: 'Bitte zuerst eine Datei auswählen.' };

  const groesse = datei.size ?? 0;
  if (groesse <= 0) return { ok: false, fehler: 'Die Datei ist leer.' };
  if (groesse > PAPIER_MAX_BYTES) {
    return { ok: false, fehler: `Die Datei ist zu groß (max. ${Math.round(PAPIER_MAX_BYTES / 1024 / 1024)} MB).` };
  }

  const typ = (datei.type || '').toLowerCase();
  if (typ === 'application/pdf' || typ.startsWith('image/')) return { ok: true };

  const endung = /\.([a-z0-9]{1,8})$/.exec((datei.name || '').toLowerCase())?.[1] ?? '';
  if (['pdf', 'jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'tif', 'tiff'].includes(endung)) {
    return { ok: true };
  }

  return { ok: false, fehler: 'Nur PDF-Dateien oder Bilder (Scan/Foto) sind zulässig.' };
};

// ─── Sortierung der Ablage ──────────────────────────────────────────────────

/**
 * Die Ablage zeigt die neueste Einweisung oben — nach NUMMER, nicht nach Datum.
 *
 * Das Datum ist frei erfassbar und wird bei nachgetragenen Papier-Einweisungen
 * regelmaessig rueckdatiert; die laufende Nummer ist dagegen das Aktenzeichen
 * und liegt fest.
 */
export const sortiereEinweisungen = <T extends { nummer: number }>(liste: T[]): T[] =>
  [...(liste ?? [])].sort((a, b) => (b.nummer || 0) - (a.nummer || 0));

// ─── Einfuegen (Ruecknahme eines Fehlgriffs) ────────────────────────────────

/**
 * Setzt einen geloeschten Punkt an seine Stelle zurueck.
 *
 * WARUM eigene Funktion und nicht „einfach neu tippen": auf der Baustelle wird
 * mit Handschuh bedient, und das Minus sitzt in Reichweite des Textfelds. Ein
 * Fehlgriff darf keine Arbeit kosten — die Oberflaeche bietet nach dem Loeschen
 * eine Ruecknahme an, und die braucht genau diese Umkehrung.
 *
 * Der Index wird eingeklemmt: liegt er ausserhalb (die Liste hat sich in der
 * Zwischenzeit veraendert), landet der Punkt am Ende statt nirgends.
 */
export const fuegePunktEin = (
  gruppen: EinweisungGruppe[],
  gruppenId: string,
  index: number,
  text: string,
): EinweisungGruppe[] =>
  ersetzeGruppe(gruppen, gruppenId, (g) => {
    const stelle = Math.max(0, Math.min(index, g.punkte.length));
    const punkte = [...g.punkte];
    punkte.splice(stelle, 0, text);
    return { ...g, punkte };
  });

/** Dieselbe Umkehrung fuer einen entfernten Teilnehmer — samt Unterschrift. */
export const fuegeTeilnehmerEin = (
  liste: EinweisungTeilnehmer[],
  index: number,
  teilnehmer: EinweisungTeilnehmer,
): EinweisungTeilnehmer[] => {
  const alt = liste ?? [];
  const stelle = Math.max(0, Math.min(index, alt.length));
  const neu = [...alt];
  neu.splice(stelle, 0, teilnehmer);
  return neu;
};

/**
 * Braucht dieses Loeschen eine Rueckfrage?
 *
 * Nur dort, wo der Fehlgriff TEUER ist: eine Unterschrift bekommt man von
 * jemandem, der die Baustelle laengst verlassen hat, nicht ein zweites Mal. Ein
 * Teilnehmer ohne Unterschrift ist dagegen in zwei Sekunden neu getippt — dort
 * genuegt die Ruecknahme, eine Rueckfrage waere nur im Weg.
 */
export const braucheTeilnehmerRueckfrage = (
  t: Pick<EinweisungTeilnehmer, 'unterschrift'> | null | undefined,
): boolean => !!t?.unterschrift;

/** Eine Gruppe mit Inhalt nimmt beim Loeschen alle ihre Punkte mit — deshalb gefragt. */
export const braucheGruppenRueckfrage = (
  g: Pick<EinweisungGruppe, 'punkte'> | null | undefined,
): boolean => (g?.punkte ?? []).some((p) => (p || '').trim().length > 0);

// ─── Entwurfssicherung fuers Funkloch ───────────────────────────────────────

/**
 * Die Sicherung des Bearbeitungsstandes auf dem GERAET.
 *
 * WARUM ueberhaupt: die Einweisung wird auf der Baustelle gefuehrt, oft ohne
 * brauchbares Netz. Faellt die Verbindung aus oder raeumt das Betriebssystem
 * den Browser weg, ist ohne Sicherung alles verloren — mitsamt Unterschriften,
 * die man von den Anwesenden nicht ein zweites Mal bekommt.
 *
 * WARUM NICHT stillschweigend einspielen: der lokale Stand kann von einem
 * abgebrochenen Versuch stammen, waehrend am Server inzwischen ein gueltiger
 * Stand steht (zweites Geraet, spaeterer Anlauf). Ein automatisches Einspielen
 * wuerde diesen ohne Nachfrage ueberschreiben. Der Anwender bekommt den
 * lokalen Stand deshalb ANGEBOTEN.
 *
 * Das Schreiben und Lesen selbst steht in einweisungEntwurf.ts — hier stehen
 * nur die Entscheidungen, und die sind pruefbar.
 */
export const ENTWURF_VERSION = 1;

/** Ein Schluessel je Einweisung — mehrere Blaetter stoeren sich nicht. */
export const entwurfSchluessel = (einweisungId: string): string =>
  `osia.einweisung.entwurf.${einweisungId || 'unbekannt'}`;

/** Der Bearbeitungsstand des Editors, roh wie in den Feldern. */
export interface EinweisungEntwurfDaten {
  titel: string;
  datum: string;
  uhrzeit: string;
  ort: string;
  /** Als Text wie im Feld — "30" und "" sind beides gueltige Zwischenstaende. */
  dauer: string;
  durchfuehrender: string;
  bemerkung: string;
  punkte: EinweisungGruppe[];
  teilnehmer: EinweisungTeilnehmer[];
  leerzeilen: number;
}

export interface EinweisungEntwurf {
  version: number;
  einweisungId: string;
  /** Zeitpunkt der lokalen Sicherung (ISO). */
  gespeichertAm: string;
  /** `updated_at` des Servers, auf dem dieser Entwurf aufsetzt. */
  serverStand: string | null;
  /** true = die Unterschriften passten nicht mehr in den Speicher. */
  ohneUnterschriften: boolean;
  daten: EinweisungEntwurfDaten;
}

export const baueEntwurf = (
  einweisungId: string,
  serverStand: string | null | undefined,
  daten: EinweisungEntwurfDaten,
  jetztIso: string,
): EinweisungEntwurf => ({
  version: ENTWURF_VERSION,
  einweisungId,
  gespeichertAm: jetztIso,
  serverStand: serverStand || null,
  ohneUnterschriften: false,
  daten,
});

/**
 * Derselbe Entwurf ohne die Unterschriften.
 *
 * Unterschriften sind PNG-DataURLs; dreissig davon sprengen den Platz, den ein
 * Browser einer Herkunft zugesteht. Lieber die Texte sichern und die Bilder
 * weglassen als gar nichts zu sichern — die Marke `ohneUnterschriften` sorgt
 * dafuer, dass das gesagt wird und dass beim Uebernehmen nicht die
 * Unterschriften des Servers geloescht werden (siehe verbindeUnterschriften).
 */
export const entwurfOhneUnterschriften = (entwurf: EinweisungEntwurf): EinweisungEntwurf => ({
  ...entwurf,
  ohneUnterschriften: true,
  daten: {
    ...entwurf.daten,
    // Bewusst Feld fuer Feld aufgezaehlt statt „alles ausser den beiden":
    // was hier NICHT steht, wird auch nicht gesichert — und genau das ist die
    // Zusage dieser Funktion.
    teilnehmer: (entwurf.daten.teilnehmer ?? []).map((t) => ({
      id: t.id,
      name: t.name,
      firma: t.firma,
    })),
  },
});

/**
 * Ist der lokale Stand neuer als der des Servers?
 *
 * Ohne `serverStand` gilt der lokale Stand als neuer: ein Datensatz ohne
 * `updated_at` sagt nichts, und ein vorhandener Entwurf ist dann die einzige
 * Aussage ueber die letzte Arbeit. Gleichstand zaehlt NICHT als neuer — dann
 * hat der Server dieselbe Arbeit bereits.
 */
export const entwurfIstNeuer = (
  entwurf: Pick<EinweisungEntwurf, 'gespeichertAm'>,
  serverStand: string | null | undefined,
): boolean => {
  if (!serverStand) return true;
  const lokal = Date.parse(entwurf?.gespeichertAm || '');
  const server = Date.parse(serverStand);
  if (Number.isNaN(lokal)) return false;
  if (Number.isNaN(server)) return true;
  return lokal > server;
};

export interface EntwurfsPruefung {
  /**
   * 'keiner'   — nichts Brauchbares da, nichts zu tun.
   * 'veraltet' — vorhanden, aber ueberholt: raeumen, nicht fragen.
   * 'anbieten' — neuer als der Server: dem Anwender vorlegen.
   */
  zustand: 'keiner' | 'veraltet' | 'anbieten';
  entwurf?: EinweisungEntwurf;
}

/**
 * Was mit dem lokal gefundenen Stand geschehen soll.
 *
 * Unbrauchbares (kaputtes JSON, aeltere Fassung des Formats) gilt als
 * 'veraltet' und wird geraeumt statt gemeldet — ein Ladefehler einer
 * HILFSsicherung darf den Editor nie aufhalten. Ein Eintrag, der zu einer
 * ANDEREN Einweisung gehoert, bleibt dagegen unangetastet ('keiner'): er ist
 * die Sicherung eines anderen Blattes.
 *
 * Ein abgeschlossenes Blatt bekommt nie ein Angebot: es ist schreibgeschuetzt,
 * der Entwurf liesse sich gar nicht mehr speichern.
 */
export const pruefeEntwurf = (
  roh: string | null | undefined,
  einweisung: Pick<SigeEinweisung, 'id' | 'status' | 'updated_at'> | null | undefined,
): EntwurfsPruefung => {
  if (!roh || !einweisung) return { zustand: 'keiner' };

  let gelesen: any;
  try {
    gelesen = JSON.parse(roh);
  } catch {
    return { zustand: 'veraltet' };
  }

  if (!gelesen || typeof gelesen !== 'object' || !gelesen.daten) return { zustand: 'veraltet' };
  if (gelesen.version !== ENTWURF_VERSION) return { zustand: 'veraltet' };
  if (gelesen.einweisungId !== einweisung.id) return { zustand: 'keiner' };
  if (einweisung.status === 'abgeschlossen') return { zustand: 'veraltet' };
  if (!entwurfIstNeuer(gelesen, einweisung.updated_at)) return { zustand: 'veraltet' };

  return { zustand: 'anbieten', entwurf: gelesen as EinweisungEntwurf };
};

/**
 * Darf fuer dieses Blatt ueberhaupt gesichert werden?
 *
 * Nein im Lesemodus und nein bei abgeschlossenen Blaettern: was sich nicht
 * speichern laesst, braucht auch keine Sicherung — sie wuerde beim naechsten
 * Oeffnen nur eine Frage stellen, die niemand beantworten kann.
 */
export const darfEntwurfSichern = (
  einweisung: Pick<SigeEinweisung, 'status'> | null | undefined,
  isReadOnly: boolean,
): boolean => !!einweisung && !isReadOnly && einweisung.status !== 'abgeschlossen';

/** "21.08.2026 um 09:41 Uhr" — der Zeitpunkt in der Angebotsfrage. */
export const beschreibeEntwurfsZeit = (iso: string | null | undefined): string => {
  const zeit = Date.parse(iso || '');
  if (Number.isNaN(zeit)) return 'unbekanntem Zeitpunkt';
  const d = new Date(zeit);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} um ${p(d.getHours())}:${p(d.getMinutes())} Uhr`;
};

/** Der Text der Angebotsfrage — vollstaendig hier, damit er pruefbar ist. */
export const entwurfsFrage = (entwurf: EinweisungEntwurf): string =>
  `Auf diesem Gerät liegt ein ungesicherter Stand von ${beschreibeEntwurfsZeit(entwurf?.gespeichertAm)}. `
  + 'Übernehmen oder verwerfen?'
  + (entwurf?.ohneUnterschriften
    ? ' Die Unterschriften wurden dabei nicht mitgesichert — sie bleiben, wie sie auf dem Server stehen.'
    : '');

/**
 * Setzt beim Uebernehmen eines Entwurfs OHNE Unterschriften die Unterschriften
 * des Servers wieder ein.
 *
 * Ohne diesen Schritt wuerde die Platzsparmassnahme aus der Sicherung zum
 * Datenverlust: der Anwender uebernaehme seine Texte und loeschte damit
 * stillschweigend jede bereits geleistete Unterschrift.
 *
 * Uebernommen wird nur, wo der Entwurf selbst keine hat — eine im Entwurf
 * enthaltene Unterschrift ist die neuere.
 */
export const verbindeUnterschriften = (
  ausEntwurf: EinweisungTeilnehmer[],
  vomServer: EinweisungTeilnehmer[],
): EinweisungTeilnehmer[] => {
  const bekannt = new Map((vomServer ?? []).map((t) => [t.id, t]));
  return (ausEntwurf ?? []).map((t) => {
    if (t.unterschrift) return t;
    const alt = bekannt.get(t.id);
    return alt?.unterschrift
      ? { ...t, unterschrift: alt.unterschrift, unterschriebenAm: alt.unterschriebenAm }
      : t;
  });
};

/** Der Stand der Gerätesicherung, wie ihn einweisungEntwurf.ts zurueckmeldet. */
export type SicherungsStand = 'gesichert' | 'ohne-unterschriften' | 'aus';

/**
 * Wie die Oberflaeche den Stand der Sicherung benennt.
 *
 * 'aus' ist bewusst eine ruhige Aussage und keine Fehlermeldung: der Editor
 * arbeitet ohne Sicherung genauso weiter, der Anwender soll nur wissen, dass er
 * sich auf sie nicht verlassen kann (privater Modus, voller Speicher).
 */
export const beschreibeSicherung = (stand: SicherungsStand): string => {
  if (stand === 'gesichert') return 'auf dem Gerät gesichert';
  if (stand === 'ohne-unterschriften') return 'gesichert — ohne Unterschriften';
  return 'keine Gerätesicherung';
};

// ─── Automatisches Speichern ────────────────────────────────────────────────

/** Ruhe nach der letzten Eingabe, bevor von selbst gespeichert wird. */
export const AUTOSAVE_RUHE_MS = 2500;
/** Abstand eines erneuten Anlaufs, nachdem einer fehlgeschlagen ist. */
export const AUTOSAVE_WIEDERHOLUNG_MS = 15000;
/** So oft wird nachgefasst, dann ist Schluss bis zur naechsten Eingabe. */
export const AUTOSAVE_MAX_VERSUCHE = 4;

export interface AutoSpeicherLage {
  /** Gibt es ueberhaupt etwas zu speichern? */
  dirty: boolean;
  /** Nur-Ansicht oder bereits abgeschlossen — dann wird gar nicht geschrieben. */
  gesperrt: boolean;
  /** Steht die Frage nach dem Geraetestand noch offen? */
  angebotOffen: boolean;
  /** Laeuft gerade ein Speichern oder Abschliessen? */
  laeuft: boolean;
  /** Wie viele automatische Anlaeufe sind seit der letzten Eingabe gescheitert? */
  fehlversuche: number;
}

/**
 * Ob und wann von selbst gespeichert wird.
 *
 * WARUM ueberhaupt automatisch: der Knopf bleibt, aber auf der Baustelle
 * drueckt ihn niemand zuverlaessig — dort wird unterschrieben, weitergelaufen
 * und das Telefon eingesteckt. Die Geraetesicherung faengt das nur auf DIESEM
 * Geraet auf; erst der Server macht den Stand fuer alle sichtbar.
 *
 * WARUM NICHT waehrend eines offenen Angebots: solange der gefundene
 * Geraetestand zur Uebernahme ansteht, wuerde ein automatisches Schreiben die
 * Entscheidung vorwegnehmen und den Fund entwerten.
 *
 * WARUM ein Deckel auf die Wiederholungen: ohne ihn liefe im Funkloch alle
 * 15 Sekunden ein Schreibversuch, bis der Akku leer ist. Nach
 * AUTOSAVE_MAX_VERSUCHE ruht es, bis der Anwender wieder etwas eingibt (dann
 * faengt die Zaehlung von vorn an) oder selbst auf Speichern drueckt.
 */
export const autoSpeicherPlan = (
  lage: AutoSpeicherLage,
): { speichern: boolean; wartezeit: number } => {
  const ruhe = { speichern: false, wartezeit: 0 };
  if (!lage.dirty || lage.gesperrt || lage.angebotOffen || lage.laeuft) return ruhe;
  const versuche = Math.max(0, Math.floor(lage.fehlversuche || 0));
  if (versuche >= AUTOSAVE_MAX_VERSUCHE) return ruhe;
  return {
    speichern: true,
    wartezeit: versuche === 0 ? AUTOSAVE_RUHE_MS : AUTOSAVE_WIEDERHOLUNG_MS,
  };
};

/**
 * Was in der Zustandszeile steht.
 *
 * Die Reihenfolge ist die Rangfolge der Dringlichkeit: ein gescheitertes
 * automatisches Speichern schlaegt alles andere, denn dann glaubt der Anwender
 * sonst, sein Blatt liege auf dem Server.
 */
export const beschreibeSpeicherstand = (lage: {
  dirty: boolean;
  laeuft: boolean;
  fehlversuche: number;
  zuletztGespeichert?: string | null;
}): { text: string; ton: 'ruhig' | 'wartend' | 'warnung' } => {
  if (lage.laeuft) return { text: 'Speichert…', ton: 'wartend' };
  if (lage.fehlversuche > 0) {
    return {
      text: lage.fehlversuche >= AUTOSAVE_MAX_VERSUCHE
        ? 'Nicht gespeichert — bitte selbst speichern'
        : 'Nicht gespeichert — neuer Versuch läuft',
      ton: 'warnung',
    };
  }
  if (lage.dirty) return { text: 'Ungespeicherte Änderungen', ton: 'wartend' };
  if (lage.zuletztGespeichert) {
    return { text: `Automatisch gespeichert ${lage.zuletztGespeichert}`, ton: 'ruhig' };
  }
  return { text: 'Gespeichert', ton: 'ruhig' };
};

// ─── Teilnehmer reihum erfassen ─────────────────────────────────────────────

/**
 * Der gefuehrte Ablauf, wenn das Geraet herumgereicht wird:
 * Name und Firma → Unterschrift → „Naechster oder beenden".
 *
 * WARUM gefuehrt statt Liste: auf der Baustelle steht die Kolonne beieinander
 * und das Telefon wandert von Hand zu Hand. Wer es bekommt, soll GENAU EINE
 * Sache vor sich haben und danach weiterreichen — nicht eine Liste, in der er
 * die Zeilen der anderen sieht, verschiebt oder loescht.
 */
export type ErfassungsSchritt = 'daten' | 'unterschrift' | 'bestaetigt';

/** Der Name traegt das Blatt — ohne ihn ist eine Unterschrift wertlos. */
export const darfZurUnterschrift = (name: string): boolean => (name || '').trim().length > 0;

/**
 * Haengt den erfassten Teilnehmer an die Liste.
 *
 * Bewusst SOFORT nach der Unterschrift und nicht erst am Ende des Reihum-Laufs:
 * faellt das Telefon aus der Hand oder raeumt das Betriebssystem den Browser
 * weg, sind die bereits geleisteten Unterschriften trotzdem im Blatt (und
 * damit in Geraetesicherung und automatischem Speichern). Wer zehn Leute
 * erfasst und beim elften abstuerzt, verliert sonst alle zehn.
 */
export const erfasseTeilnehmer = (
  liste: EinweisungTeilnehmer[],
  eingabe: { id: string; name: string; firma?: string; unterschrift?: string; jetztIso: string },
): EinweisungTeilnehmer[] => {
  const person = neuerTeilnehmer(eingabe.id, eingabe.name, eingabe.firma || '');
  const mit = [...(liste ?? []), person];
  return eingabe.unterschrift
    ? setzeUnterschrift(mit, eingabe.id, eingabe.unterschrift, eingabe.jetztIso)
    : mit;
};

/**
 * Womit das Formular fuer den NAECHSTEN vorbelegt wird.
 *
 * Der Name ist immer leer — er gehoert der Person, die das Geraet gerade
 * bekommt. Die Firma bleibt stehen: eine Kolonne kommt aus demselben Betrieb,
 * und wer sie fuenfmal hintereinander tippt, laesst sie beim sechsten Mal weg.
 * Ueberschreiben kann sie jeder.
 */
export const naechsteErfassung = (letzteFirma?: string): { name: string; firma: string } => ({
  name: '',
  firma: (letzteFirma || '').trim(),
});

/** Zaehlstand fuer die Bestaetigungsseite („3 erfasst, davon 2 unterschrieben"). */
export const beschreibeErfassungsStand = (liste: EinweisungTeilnehmer[]): string => {
  const anzahl = (liste ?? []).length;
  const signiert = zaehleUnterschriften(liste);
  const leute = anzahl === 1 ? '1 Teilnehmer' : `${anzahl} Teilnehmer`;
  return signiert === anzahl
    ? `${leute} erfasst`
    : `${leute} erfasst, davon ${signiert} unterschrieben`;
};

// ─── Online-Einweisung per Link/QR ──────────────────────────────────────────
//
// Vorbild: components/sifa/unterweisungModulLogic.ts (linkIstAktiv, linkUrl,
// qrDateiname, aushangVorgabe). Die Online-Teilnahmen liegen in einer EIGENEN
// Tabelle (docs/sige-einweisung-online-plan.md §1.1); Anzeige, Zaehler und
// PDF arbeiten mit der VEREINIGTEN Liste aus Geraet + online.

/**
 * Ist der Link gerade nutzbar? Token vorhanden und nicht abgelaufen.
 *
 * Kein eigenes boolean in der Datenbank — dasselbe Muster wie
 * sifa_unterweisungen: `link_token is not null and (link_aktiv_bis is null
 * or link_aktiv_bis > now())`. Ein unlesbares Ablaufdatum sperrt NICHT: die
 * Datenbank ist die verbindliche Stelle, hier geht es um die Anzeige.
 */
export const linkIstAktiv = (
  e: Pick<SigeEinweisung, 'link_token' | 'link_aktiv_bis'>,
  jetzt: Date = new Date(),
): boolean => {
  if (!(e.link_token || '').trim()) return false;
  if (!e.link_aktiv_bis) return true;
  const bis = Date.parse(e.link_aktiv_bis);
  return Number.isNaN(bis) ? true : bis > jetzt.getTime();
};

/**
 * Der Link darf nur auf ein EINGEFRORENES Blatt zeigen (Plan §1.2).
 *
 * Die Unterschrift gilt fuer den Inhalt, der gezeigt wurde; ein aktiver Link
 * auf einen noch aenderbaren Entwurf hiesse, Person A unterschreibt Fassung
 * 1 und Person B Fassung 2. Der harte Riegel sitzt in der RPC
 * `sige_einweisung_link_setzen` — hier wird nur der Knopf entschieden.
 */
export const darfLinkAktivieren = (e: Pick<SigeEinweisung, 'status'>): boolean =>
  e.status === 'abgeschlossen';

/**
 * Der oeffentliche Link. `pathname` bleibt drin, weil die App nicht zwingend
 * an der Wurzel liegt; der Hash-Router (`#/einweisung/<token>`) liegt neben
 * `#/unterweisung/` in index.tsx.
 */
export const einweisungLinkUrl = (origin: string, pathname: string, token: string): string =>
  `${origin}${pathname || '/'}#/einweisung/${encodeURIComponent(token)}`;

/** Titel ohne Zeichen, die ein Dateisystem stoeren — hoechstens 40 Zeichen. */
const dateiSauber = (titel: string | null | undefined): string =>
  (titel || '')
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 40);

/** Dateiname des QR-PNGs: `QR_Einweisung_03_Titel.png`. */
export const einweisungQrDateiname = (e: Pick<SigeEinweisung, 'nummer' | 'titel'>): string =>
  `QR_Einweisung_${String(e.nummer).padStart(2, '0')}_${dateiSauber(e.titel) || 'SiGe-Plan'}.png`;

/** Dateiname des Aushang-PDFs, gleiche Bereinigung wie beim QR-PNG. */
export const einweisungAushangDateiname = (e: Pick<SigeEinweisung, 'nummer' | 'titel'>): string =>
  einweisungQrDateiname(e).replace(/^QR_/, 'Aushang_').replace(/\.png$/, '.pdf');

/**
 * Vorgabe fuer den Aushang (Ueberschrift + Kurztext), vom Innendienst vor dem
 * Druck aenderbar. Bauvorhaben und Planbezug stehen mit drauf, damit ein
 * abgehaengtes Blatt noch sagt, zu welcher Baustelle es gehoert.
 */
export const aushangVorgabeEinweisung = (
  e: Pick<SigeEinweisung, 'nummer' | 'titel' | 'plan_nr' | 'plan_stand'>,
  projektName?: string | null,
): { ueberschrift: string; text: string } => {
  const bauvorhaben = (projektName || '').trim();
  const planTeile: string[] = [];
  if ((e.plan_nr || '').trim()) planTeile.push(`SiGe-Plan Nr. ${e.plan_nr!.trim()}`);
  if ((e.plan_stand || '').trim()) planTeile.push(`Stand ${e.plan_stand!.trim()}`);
  const titel = (e.titel || '').trim();
  const absaetze = [
    bauvorhaben ? `Bauvorhaben: ${bauvorhaben}` : '',
    'QR-Code scannen, Einweisung lesen, Name eintragen und unterschreiben.',
    [`Einweisung Nr. ${e.nummer}${titel ? ` — ${titel}` : ''}`, ...planTeile].join(' · '),
  ].filter(Boolean);
  return {
    ueberschrift: 'Einweisung in den SiGe-Plan',
    text: absaetze.join('\n\n'),
  };
};

/** „Vorname Nachname" — ohne Doppelleerzeichen, nie leer. */
export const onlineTeilnehmerName = (
  t: Pick<EinweisungOnlineTeilnahme, 'vorname' | 'nachname'>,
): string =>
  [t.vorname, t.nachname].map((s) => (s || '').trim()).filter(Boolean).join(' ') || 'Unbekannt';

/**
 * Eine Online-Teilnahme in der Form, die Liste und PDF kennen.
 *
 * Die id bekommt das Praefix `online:`, damit sie nie mit einer Geraete-Zeile
 * zusammenfaellt (beide sind UUIDs aus verschiedenen Quellen).
 */
export const onlineAlsTeilnehmer = (t: EinweisungOnlineTeilnahme): EinweisungTeilnehmer => ({
  id: `online:${t.id}`,
  name: onlineTeilnehmerName(t),
  funktion: (t.funktion || '').trim() || undefined,
  firma: (t.firma || '').trim() || undefined,
  unterschrift: t.unterschrift || undefined,
  unterschriebenAm: t.unterschrieben_am,
  quelle: 'online',
});

const zeitpunkt = (iso: string | null | undefined): number => Date.parse(iso || '') || 0;

/**
 * Die VEREINIGTE Liste: Geraet zuerst (in der Reihenfolge des Blattes), danach
 * die Online-Teilnahmen nach Zeitpunkt aufsteigend — so, wie die Leute
 * nacheinander unterschrieben haben.
 */
export const vereinigeTeilnehmer = (
  e: Pick<SigeEinweisung, 'teilnehmer'>,
  online: EinweisungOnlineTeilnahme[],
): EinweisungTeilnehmer[] => [
  ...(e.teilnehmer ?? []),
  ...[...(online ?? [])]
    .sort((a, b) => zeitpunkt(a.unterschrieben_am) - zeitpunkt(b.unterschrieben_am))
    .map(onlineAlsTeilnehmer),
];

/**
 * Zaehlstand fuer Liste und Kopfzeile („7 (3 online)").
 *
 * `unterschrieben` zaehlt ueber BEIDE Quellen: eine Online-Teilnahme hat
 * immer eine Unterschrift (die RPC nimmt keine ohne an), eine Geraete-Zeile
 * nicht zwingend.
 */
export const zaehleTeilnahmen = (
  e: Pick<SigeEinweisung, 'teilnehmer'>,
  online: EinweisungOnlineTeilnahme[],
): { geraet: number; online: number; gesamt: number; unterschrieben: number } => {
  const geraet = (e.teilnehmer ?? []).length;
  const onlineAnzahl = (online ?? []).length;
  const unterschrieben =
    zaehleUnterschriften(e.teilnehmer ?? []) + (online ?? []).filter((t) => !!t.unterschrift).length;
  return { geraet, online: onlineAnzahl, gesamt: geraet + onlineAnzahl, unterschrieben };
};

/** Eine Abfrage je Projekt, danach je Einweisung verteilt — fuer die Ablage in einem Durchlauf. */
export const gruppiereOnlineJeEinweisung = (
  liste: EinweisungOnlineTeilnahme[],
): Record<string, EinweisungOnlineTeilnahme[]> => {
  const gruppen: Record<string, EinweisungOnlineTeilnahme[]> = {};
  for (const t of liste ?? []) {
    (gruppen[t.einweisung_id] ||= []).push(t);
  }
  return gruppen;
};

/** Die Teilnahmen-Ansicht zeigt die neueste zuerst. */
export const sortiereOnlineTeilnahmen = (
  liste: EinweisungOnlineTeilnahme[],
): EinweisungOnlineTeilnahme[] =>
  [...(liste ?? [])].sort((a, b) => zeitpunkt(b.unterschrieben_am) - zeitpunkt(a.unterschrieben_am));
